import {displayLatestGfr} from '@/modules/patients/functions'
import {getFullName} from '@/modules/users/functions'
import {useGetSettings} from '@/modules/settings/api'
import {Avatar, Badge, Box, Flex, HStack, StackSeparator, Text, Tooltip} from '@chakra-ui/react'
import {PatientSource} from '@cypro/engine/database/schema'
import {Patient} from '@cypro/engine/schema'
import {getAge, getDatetimeFormatter, getShortDateFormatter, multipleDigitsNumberFormatter, isDefined} from '@cypro/ui/utils'
import {useTranslate} from '@tolgee/react'
import {useMemo} from 'react'
import {LuContactRound, LuInfo} from 'react-icons/lu'

export function PatientInfoHeader({patient}: {patient: Patient}) {
  const {t} = useTranslate('common')
  const {settings} = useGetSettings()

  const characteristics = patient.characteristics?.[0]
  const encounter = patient.encounters?.at(-1)
  const isHL7 = patient.source === PatientSource.enum.hl7_message || patient.source === PatientSource.enum.hl7_import

  const basicInfoItems = useMemo(
    () => [
      patient.sex && t(patient.sex),
      isDefined(patient.birthDate)
        ? getShortDateFormatter().format(patient.birthDate) + ` (${getAge(patient.birthDate)} ${t('yrs')})`
        : null,
      characteristics?.diagnosis?.name,
    ],
    [patient, characteristics, t]
  )

  const infoBoxItems = useMemo(() => {
    const value = [
      {
        label: t('characteristics.weight'),
        value: characteristics?.weight ? multipleDigitsNumberFormatter.format(characteristics?.weight) : '-',
        updatedAt: characteristics?.updatedAt,
      },
      {
        label: t('characteristics.height'),
        value: characteristics?.height ? multipleDigitsNumberFormatter.format(characteristics?.height) : '-',
        updatedAt: characteristics?.updatedAt,
      },
      {
        label: t('characteristics.bsa'),
        value: characteristics?.bsa ? multipleDigitsNumberFormatter.format(characteristics?.bsa) : '-',
        updatedAt: characteristics?.updatedAt,
      },
      {
        label: t('characteristics.gfr'),
        value: displayLatestGfr(patient),
        updatedAt: patient.gfrs?.[0]?.createdAt,
      },
      {
        label: t('responsible_doctor'),
        value: getFullName(patient.responsibleDoctor, 'last-first'),
        showValueTooltip: true,
      },
      {
        label: t('ward'),
        value: encounter?.ward?.name,
        showValueTooltip: true,
      },
      {
        label: t('location'),
        value: patient.responsibleDoctor?.userAccessLocations?.[0]?.location?.name,
        showValueTooltip: true,
      },
    ]

    const showHospitalPatientId = settings?.showHospitalPatientId ?? true
    if (showHospitalPatientId)
      value.push({label: t('hospital_id'), value: patient.hospitalPatientId || '', showValueTooltip: true})

    return value
  }, [t, characteristics, encounter, settings, patient])

  return (
    <Box
      paddingTop="4"
      paddingBottom="4"
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
    >
      <HStack gapX="3">
        <Avatar.Root size="xl" shape="square" rounded="md">
          <LuContactRound size="1.5rem" />
        </Avatar.Root>
        <Box>
          <Flex gap={2}>
            <Text fontSize="lg" fontWeight="semibold">
              {`${patient.title ? t(patient.title) : ''} ${patient.givenName} ${patient.familyName}`}
            </Text>

            {isHL7 && (
              <Badge variant="surface" colorPalette="blue">
                HL7
              </Badge>
            )}
          </Flex>
          <SummaryInfoLabel values={basicInfoItems} />
        </Box>
      </HStack>
      <HStack gap={{base: 3, '2xl': 6}}>
        {infoBoxItems.map(item => {
          return (
            <InfoBox
              key={item.label}
              label={item.label}
              value={item.value}
              updatedAt={item.updatedAt}
              showValueTooltip={item.showValueTooltip}
            />
          )
        })}
      </HStack>
    </Box>
  )
}

function SummaryInfoLabel({values}: {values: (number | string | undefined | null)[]}) {
  const output = values.filter(item => item !== null && item !== undefined)

  if (output.length === 0) return <Text color="fg.muted">-</Text>

  return (
    <HStack gap="2" separator={<StackSeparator />}>
      {output.map(item => (
        <Text key={item} fontSize="xs" fontWeight="medium" color="fg.muted" textTransform="capitalize">
          {item}
        </Text>
      ))}
    </HStack>
  )
}

type InfoBoxProps = {
  label: string
  value?: string | number | null
  updatedAt?: number | null
  showValueTooltip?: boolean
}

function InfoBox({label, value, updatedAt, showValueTooltip = false}: InfoBoxProps) {
  const {t} = useTranslate('common')

  return (
    <Box maxWidth={100} cursor="default">
      <Tooltip.Root disabled={!updatedAt}>
        <Tooltip.Trigger asChild>
          <HStack color="fg.muted">
            <Text fontSize={{base: '2xs', '2xl': 'xs'}} fontWeight="normal" textTransform="uppercase">
              {label}
            </Text>
            {updatedAt && (
              <Box hideBelow="xl">
                <LuInfo size={12} />
              </Box>
            )}
          </HStack>
        </Tooltip.Trigger>
        <Tooltip.Positioner>
          <Tooltip.Content>{`${t('last_update')}: ${getDatetimeFormatter().format(updatedAt as number)}`}</Tooltip.Content>
        </Tooltip.Positioner>
      </Tooltip.Root>
      <Tooltip.Root disabled={!showValueTooltip || !value}>
        <Tooltip.Trigger asChild>
          <Text fontSize={{base: 'xs', '2xl': 'sm'}} lineClamp={1}>
            {value || '-'}
          </Text>
        </Tooltip.Trigger>
        <Tooltip.Positioner>
          <Tooltip.Content>{value}</Tooltip.Content>
        </Tooltip.Positioner>
      </Tooltip.Root>
    </Box>
  )
}
